// ══════════════════════════════════════════════════════════════════════════════
// RamayanaClubPage.js — Ramayana Book Club
// Pages: home → kanda → scene (one per page) → debate
// Beautiful, minimal. Image-ready at the top of every scene.
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// ─────────────────────────────────────────────────────────────────────────────
// HOME — kanda grid, like Browse by Theme
// ─────────────────────────────────────────────────────────────────────────────
function HomePage({ user, kandas, myClubKanda, onSelectKanda }) {
  const [hovered, setHovered] = useState(null);
  return (
    <div>
      <div className="section-title">Ramayana Club</div>
      <div className="section-sub">Seven kandas. Ten strangers. One story. See your life in it.</div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: "16px", marginBottom: "32px" }}>
        {kandas.map(k => {
          const isMyClub = k.id === myClubKanda;
          const locked   = k.coming_soon;
          const isHov    = hovered === k.id;
          return (
            <div
              key={k.id}
              onClick={locked ? undefined : () => onSelectKanda(k.id)}
              onMouseEnter={() => !locked && setHovered(k.id)}
              onMouseLeave={() => setHovered(null)}
              style={{
                position: "relative", overflow: "hidden",
                background: isHov
                  ? `linear-gradient(160deg, var(--bg2), ${k.color_accent}20)`
                  : `linear-gradient(160deg, var(--bg2), var(--bg3))`,
                border: `1px solid ${isMyClub ? k.color_accent + "70" : isHov ? k.color_accent + "60" : "var(--border)"}`,
                borderRadius: "18px", padding: "26px 24px 22px",
                cursor: locked ? "default" : "pointer",
                opacity: locked ? 0.45 : 1,
                transition: "all 0.25s",
                boxShadow: isHov ? `0 8px 32px ${k.color_accent}22` : "0 2px 12px rgba(201,149,58,0.06)",
              }}
            >
              {/* Colored top accent bar */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: 3,
                background: `linear-gradient(90deg, ${k.color_accent}, ${k.color_accent}55, transparent)`,
              }} />
              {/* Glow orb on hover */}
              <div style={{
                position: "absolute", top: -40, right: -40, width: 160, height: 160,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${k.color_accent}22 0%, transparent 70%)`,
                pointerEvents: "none", opacity: isHov ? 1 : 0, transition: "opacity 0.25s",
              }} />

              {/* Icon */}
              <div style={{ fontSize: "2.4rem", marginBottom: "14px" }}>{k.icon}</div>

              {/* Name + badges */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px", flexWrap: "wrap" }}>
                <span style={{
                  fontFamily: "'Inter',sans-serif", fontSize: "1.2rem", fontWeight: 700,
                  color: "var(--saffron)",
                  transition: "color 0.25s",
                }}>
                  {k.name}
                </span>
                {isMyClub && (
                  <span style={{
                    fontSize: "0.95rem", background: "var(--saffron)",
                    color: "#fff", borderRadius: "20px",
                    padding: "3px 10px", border: "1px solid var(--saffron)", fontWeight: 600,
                  }}>Your club</span>
                )}
                {locked && (
                  <span style={{
                    fontSize: "0.95rem", color: "var(--text-dim)",
                    background: "var(--bg3)", borderRadius: "20px", padding: "3px 10px",
                    border: "1px solid var(--border)",
                  }}>Coming soon</span>
                )}
              </div>

              {/* Tagline */}
              <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginBottom: "10px", lineHeight: 1.55 }}>
                {k.tagline}
              </div>

              {/* Hook */}
              <div style={{ fontSize: "1rem", color: "var(--text)", lineHeight: 1.7, marginBottom: "18px" }}>
                {k.hook}
              </div>

              {!locked && (
                <div style={{ fontSize: "0.95rem", color: isHov ? "var(--saffron)" : "var(--text-dim)", transition: "color 0.25s", fontWeight: isHov ? 600 : 400 }}>
                  {isMyClub ? "Continue reading →" : "Explore kanda →"}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* How it works */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#E8832A,#FFD060,#C9953A)" }} />
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", fontWeight: 700, color: "var(--gold)", marginBottom: "18px" }}>
          How it works
        </div>
        {[
          ["📖", "One scene at a time — read slowly, not quickly"],
          ["💬", "After each scene, ask your group a question"],
          ["⚔️", "Halfway through, debate a character's choice"],
          ["👥", "10 strangers — honesty is easier with people you don't know"],
        ].map(([icon, text]) => (
          <div key={text} style={{ display: "flex", gap: "12px", marginBottom: "13px", fontSize: "1rem", color: "var(--text-dim)", lineHeight: 1.6 }}>
            <span style={{ flexShrink: 0, fontSize: "1.2rem" }}>{icon}</span><span>{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KANDA PAGE — dark hero + big scene cards
// ─────────────────────────────────────────────────────────────────────────────
function KandaPage({ user, kanda, clubData, onJoin, onOpenScene, onBack, onOpenCommunity }) {
  const [joining, setJoining] = useState(false);
  const [error,   setError]   = useState("");
  const [members, setMembers] = useState([]);
  const isMember  = !!clubData;
  const scenes    = clubData?.scenes || [];
  const readSet   = new Set(scenes.filter(s => s.read).map(s => s.id));
  const progress  = scenes.length ? Math.round((readSet.size / scenes.length) * 100) : 0;
  const firstUnread = scenes.findIndex(s => !s.read);
  const resumeAt    = firstUnread === -1 ? scenes.length - 1 : firstUnread;
  const accent = "#E8832A"; // one accent color (var(--saffron)) across the app

  useEffect(() => {
    if (!clubData?.club_id) return;
    fetch(`${API}/ramayana/members/${clubData.club_id}?user_id=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => Array.isArray(d) ? setMembers(d) : [])
      .catch(() => {});
  }, [clubData?.club_id]);

  const handleJoin = async () => {
    setJoining(true); setError("");
    try {
      const res = await fetch(`${API}/ramayana/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, display_name: user.name, kanda: kanda.id }),
      });
      const d = await res.json();
      if (d.error) setError(d.error);
      else onJoin(d);
    } catch { setError("Could not reach server."); }
    setJoining(false);
  };

  return (
    <div>
      <button onClick={onBack} style={{
        background: "none", border: "none", color: "var(--text-dim)",
        cursor: "pointer", fontSize: "1rem", padding: "0",
        marginBottom: "20px", display: "flex", alignItems: "center", gap: "6px",
      }}>
        ← All Kandas
      </button>

      {/* Kanda hero */}
      <div style={{
        position: "relative", overflow: "hidden",
        borderRadius: "20px", padding: "28px 24px 24px",
        marginBottom: "24px",
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{
          position: "absolute", right: "-14px", top: "-14px",
          fontSize: "9rem", opacity: 0.07, userSelect: "none", pointerEvents: "none", lineHeight: 1,
        }}>
          {kanda.icon}
        </div>

        <div style={{
          display: "inline-flex", alignItems: "center", gap: "7px",
          background: accent + "18", border: `1px solid ${accent}40`,
          borderRadius: "20px", padding: "5px 15px",
          fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", fontWeight: 600,
          color: accent, marginBottom: "16px",
        }}>
          {kanda.icon} Ramayana
        </div>

        <div style={{
          fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "clamp(1.3rem,5vw,1.75rem)", color: "var(--text)",
          marginBottom: "10px", lineHeight: 1.2,
        }}>
          {kanda.name}
        </div>

        <div style={{
          fontFamily: "'Inter',sans-serif", fontSize: "1rem",
          color: "var(--text-dim)", lineHeight: 1.65, marginBottom: "10px",
        }}>
          {kanda.hook}
        </div>

        <div style={{ fontSize: "0.95rem", color: accent, fontWeight: 600 }}>
          {kanda.emotion}
        </div>

        {isMember && scenes.length > 0 && (
          <div style={{ marginTop: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "6px" }}>
              <span>{readSet.size} of {scenes.length} scenes read</span>
              <span style={{ color: accent, fontWeight: 600 }}>{progress}%</span>
            </div>
            <div style={{ height: "5px", background: "var(--bg3)", borderRadius: "3px" }}>
              <div style={{
                height: "100%", width: `${progress}%`,
                background: accent,
                borderRadius: "3px", transition: "width 0.5s",
              }} />
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      {!isMember ? (
        <div style={{ marginBottom: "28px" }}>
          <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginBottom: "16px", lineHeight: 1.65 }}>
            You'll be matched with up to 9 other readers. Read the same scenes. Ask each other questions.
          </div>
          {error && <div style={{ color: "#dc2626", fontSize: "0.95rem", marginBottom: "10px" }}>{error}</div>}
          <button onClick={handleJoin} disabled={joining} style={{
            background: accent,
            color: "#fff", border: "none", borderRadius: "12px",
            padding: "13px 32px", fontSize: "0.95rem", fontWeight: 700,
            cursor: joining ? "wait" : "pointer",
          }}>
            {joining ? "Joining..." : `Join ${kanda.name} →`}
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: "10px", marginBottom: "28px", flexWrap: "wrap" }}>
          <button onClick={() => onOpenScene(resumeAt)} style={{
            background: accent,
            color: "#fff", border: "none", borderRadius: "12px",
            padding: "12px 24px", fontSize: "0.95rem", fontWeight: 700,
            cursor: "pointer", flex: 1,
          }}>
            {readSet.size === 0
              ? `Begin ${kanda.name} →`
              : readSet.size === scenes.length
              ? "All scenes read ✓"
              : `Continue · Scene ${resumeAt + 1} →`}
          </button>
          <button onClick={onOpenCommunity} style={{
            background: "#fff", border: `1px solid ${accent}`,
            color: accent, borderRadius: "12px",
            padding: "12px 20px", fontSize: "0.95rem", fontWeight: 600,
            cursor: "pointer", display: "flex", alignItems: "center", gap: "7px",
          }}>
            💬 Group
          </button>
        </div>
      )}

      {/* Member roster */}
      {isMember && members.length > 0 && (
        <div style={{
          background: "var(--bg2)", border: "1px solid var(--border)",
          borderRadius: "16px", padding: "16px 18px", marginBottom: "24px",
          boxShadow: "var(--card-shadow)",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", fontWeight: 700, color: accent }}>
              Your group · {members.length} readers
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {members.map(m => {
              const isMe = m.user_id === user.id;
              const bg   = avatarColor(m.display_name);
              return (
                <div key={m.user_id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", minWidth: "52px" }}>
                  <div style={{
                    width: "44px", height: "44px", borderRadius: "50%",
                    background: bg, display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "1rem", fontWeight: 700, color: "#fff",
                    fontFamily: "'Inter',sans-serif",
                    border: isMe ? `2px solid ${accent}` : "2px solid transparent",
                    boxShadow: isMe ? `0 0 0 2px ${accent}40` : "none",
                  }}>
                    {(m.display_name || "?")[0].toUpperCase()}
                  </div>
                  <div style={{ fontSize: "0.95rem", color: isMe ? accent : "var(--text-dim)", textAlign: "center", maxWidth: "52px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {isMe ? "You" : m.display_name.split(" ")[0]}
                  </div>
                  <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                    {m.scenes_read}/{scenes.length}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scene list */}
      {isMember && scenes.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", fontWeight: 700, color: accent }}>
              All scenes
            </div>
            <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {scenes.map((scene, i) => {
              const isRead = readSet.has(scene.id);
              return (
                <div
                  key={scene.id}
                  onClick={() => onOpenScene(i)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = accent + "60"; e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = isRead ? accent + "40" : "var(--border)"; e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: "14px",
                    background: "var(--bg2)",
                    border: `1px solid ${isRead ? accent + "40" : "var(--border)"}`,
                    borderRadius: "14px", padding: "16px 18px",
                    cursor: "pointer", transition: "border-color 0.2s, box-shadow 0.2s",
                    boxShadow: "var(--card-shadow)",
                  }}
                >
                  <div style={{
                    width: "36px", height: "36px", borderRadius: "10px", flexShrink: 0,
                    background: isRead ? accent + "20" : "var(--bg3)",
                    border: `1px solid ${isRead ? accent : "var(--border)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "'Inter',sans-serif",
                    fontSize: isRead ? "1rem" : "0.95rem",
                    color: isRead ? accent : "var(--text-dim)", fontWeight: 700,
                  }}>
                    {isRead ? "✓" : i + 1}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: "1rem", color: isRead ? accent : "var(--text)",
                      marginBottom: "5px", lineHeight: 1.3,
                    }}>
                      {scene.title}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                        {scene.emotion}
                      </div>
                      {scene.club_reads > 0 && (
                        <div style={{
                          fontSize: "0.95rem", color: accent,
                          background: accent + "12", borderRadius: "20px",
                          padding: "1px 9px", border: `1px solid ${accent}30`,
                        }}>
                          {scene.club_reads} read
                        </div>
                      )}
                    </div>
                  </div>

                  <span style={{ color: isRead ? accent + "70" : "var(--text-dim)", fontSize: "1rem", marginTop: "6px" }}>›</span>
                </div>
              );
            })}
          </div>

          {clubData.debate?.question && (
            <div
              onClick={() => onOpenScene("debate")}
              style={{
                marginTop: "12px",
                display: "flex", alignItems: "center", gap: "14px",
                background: "#fff",
                border: "1px solid rgba(180,40,40,0.3)",
                borderRadius: "14px", padding: "16px 18px", cursor: "pointer",
                boxShadow: "var(--card-shadow)",
              }}
            >
              <div style={{
                width: "36px", height: "36px", borderRadius: "10px",
                background: "rgba(180,40,40,0.1)", border: "1px solid rgba(180,40,40,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.2rem", flexShrink: 0,
              }}>⚔️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#b91c1c", marginBottom: "4px" }}>The Debate</div>
                <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                  {clubData.debate.character} — {clubData.debate.question}
                </div>
              </div>
              <span style={{ color: "rgba(180,40,40,0.6)", fontSize: "1rem" }}>›</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMMUNITY PAGE — full-page social discussion for a scene
// ─────────────────────────────────────────────────────────────────────────────
function avatarColor(name) {
  const palette = ["#E8832A","#8B5CF6","#3a7bbf","#5D8A3C","#C0392B","#D4731A","#F0C040","#e91e8c"];
  let h = 0;
  for (const c of (name || "?")) h = ((h << 5) - h) + c.charCodeAt(0);
  return palette[Math.abs(h) % palette.length];
}

function timeAgo(iso) {
  if (!iso) return "";
  // Server stores UTC without "Z" — force UTC parsing to avoid timezone shift
  const utcStr = iso.endsWith("Z") ? iso : iso + "Z";
  const diff = Math.floor((Date.now() - new Date(utcStr)) / 1000);
  if (diff < 10)    return "just now";
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function Avatar({ name, size = 38, isAi = false }) {
  const bg = isAi ? "var(--gold)" : avatarColor(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: bg, display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.37, color: "#fff", fontWeight: 700,
      fontFamily: "'Inter',sans-serif",
    }}>
      {isAi ? "🕉" : (name || "?")[0].toUpperCase()}
    </div>
  );
}

// scenes prop = full scene list for context labels + compose picker
function CommunityPage({ clubId, kanda, user, scenes, fromScene, onBack }) {
  const [feed,      setFeed]      = useState([]);
  const [members,   setMembers]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState(null);
  const [answers,   setAnswers]   = useState({});
  const [replyText, setReplyText] = useState({});
  const [newQ,      setNewQ]      = useState("");
  const [newQScene, setNewQScene] = useState(fromScene?.id || "");
  const [postingQ,  setPostingQ]  = useState(false);
  const [postingA,  setPostingA]  = useState(false);
  const [filter,    setFilter]    = useState(fromScene ? fromScene.id : "all");
  const accent = "#E8832A"; // one accent color (var(--saffron)) across the app

  // Scene lookup map
  const sceneMap = Object.fromEntries((scenes || []).map(s => [s.id, s]));

  const loadFeed = () => {
    if (!clubId) return;
    fetch(`${API}/ramayana/feed/${clubId}?user_id=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { setFeed(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    loadFeed();
    fetch(`${API}/ramayana/members/${clubId}?user_id=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => Array.isArray(d) ? setMembers(d) : [])
      .catch(() => {});
  }, [clubId]); // eslint-disable-line

  const loadAnswers = async (qid) => {
    const res = await fetch(`${API}/ramayana/answers/${qid}?user_id=${encodeURIComponent(user.id)}`);
    const d   = await res.json();
    setAnswers(prev => ({ ...prev, [qid]: Array.isArray(d) ? d : [] }));
  };

  const toggleExpand = (qid) => {
    if (expanded === qid) { setExpanded(null); return; }
    setExpanded(qid);
    if (!answers[qid]) loadAnswers(qid);
  };

  const postQuestion = async () => {
    if (!newQ.trim() || !newQScene || postingQ) return;
    setPostingQ(true);
    await fetch(`${API}/ramayana/question`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, display_name: user.name, scene_id: newQScene, question: newQ.trim() }),
    });
    setNewQ(""); loadFeed(); setPostingQ(false);
  };

  const postAnswer = async (qid) => {
    const txt = (replyText[qid] || "").trim();
    if (!txt || postingA) return;
    setPostingA(true);
    await fetch(`${API}/ramayana/answer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question_id: qid, user_id: user.id, display_name: user.name, text: txt }),
    });
    setReplyText(prev => ({ ...prev, [qid]: "" }));
    await loadAnswers(qid);
    loadFeed();
    setPostingA(false);
  };

  const visibleFeed = filter === "all" ? feed : feed.filter(q => q.scene_id === filter);

  return (
    <div>
      {/* Back */}
      <button onClick={onBack} style={{
        background: "none", border: "none", color: "var(--text-dim)",
        cursor: "pointer", fontSize: "0.95rem", padding: "0",
        marginBottom: "20px", display: "flex", alignItems: "center", gap: "6px",
      }}>
        ← {kanda.name}
      </button>

      {/* Kanda header */}
      <div style={{
        borderRadius: "14px", padding: "14px 18px", marginBottom: "20px",
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        boxShadow: "var(--card-shadow)",
        display: "flex", alignItems: "center", gap: "12px",
      }}>
        <span style={{ fontSize: "1.8rem" }}>{kanda.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", fontWeight: 700, color: accent, marginBottom: "3px" }}>
            {kanda.name} · Group Discussion
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
            {members.length} readers · {feed.length} questions
          </div>
        </div>
      </div>

      {/* Member avatars row */}
      {members.length > 0 && (
        <div style={{
          background: "var(--bg2)", border: "1px solid var(--border)",
          borderRadius: "14px", padding: "14px 16px", marginBottom: "20px",
        }}>
          <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--text-dim)", marginBottom: "10px" }}>
            Your group
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {members.map(m => {
              const isMe = m.user_id === user.id;
              return (
                <div key={m.user_id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                  <div style={{
                    width: "40px", height: "40px", borderRadius: "50%",
                    background: avatarColor(m.display_name),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.95rem", fontWeight: 700, color: "#fff",
                    border: isMe ? `2px solid ${accent}` : "2px solid transparent",
                    position: "relative",
                  }}>
                    {(m.display_name || "?")[0].toUpperCase()}
                    {m.scenes_read > 0 && (
                      <div style={{
                        position: "absolute", bottom: -1, right: -1,
                        width: "14px", height: "14px", borderRadius: "50%",
                        background: accent, border: "1.5px solid var(--bg2)",
                        fontSize: "0.48rem", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 700,
                      }}>
                        {m.scenes_read}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: "0.95rem", color: isMe ? accent : "var(--text-dim)", maxWidth: "52px", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {isMe ? "You" : m.display_name.split(" ")[0]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter tabs — All + per-scene */}
      <div style={{ display: "flex", gap: "7px", flexWrap: "wrap", marginBottom: "20px", overflowX: "auto", paddingBottom: "2px" }}>
        <button
          onClick={() => setFilter("all")}
          style={{
            background: filter === "all" ? accent : "var(--bg2)",
            border: `1px solid ${filter === "all" ? accent : "var(--border)"}`,
            borderRadius: "20px", padding: "6px 14px",
            fontSize: "0.95rem", color: filter === "all" ? "#fff" : "var(--text-dim)",
            cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          All scenes
        </button>
        {(scenes || []).filter(s => feed.some(q => q.scene_id === s.id)).map(s => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            style={{
              background: filter === s.id ? accent : "var(--bg2)",
              border: `1px solid ${filter === s.id ? accent : "var(--border)"}`,
              borderRadius: "20px", padding: "6px 14px",
              fontSize: "0.95rem", color: filter === s.id ? "#fff" : "var(--text-dim)",
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            Scene {(scenes || []).indexOf(s) + 1}
          </button>
        ))}
      </div>

      {/* Compose — new question */}
      <div style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: "16px", padding: "16px 18px", marginBottom: "24px",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
          <Avatar name={user.name} size={40} />
          <div style={{ flex: 1 }}>
            {/* Scene picker */}
            <select
              value={newQScene}
              onChange={e => setNewQScene(e.target.value)}
              style={{
                width: "100%", background: "var(--bg3)", border: "1px solid var(--border)",
                borderRadius: "8px", padding: "8px 10px", color: newQScene ? "var(--text)" : "var(--text-dim)",
                fontSize: "0.95rem", marginBottom: "8px", outline: "none",
              }}
            >
              <option value="">About which scene?</option>
              {(scenes || []).map((s, i) => (
                <option key={s.id} value={s.id}>Scene {i + 1} — {s.title}</option>
              ))}
            </select>
            <textarea
              value={newQ}
              onChange={e => setNewQ(e.target.value)}
              placeholder="Ask your group something…"
              rows={2}
              style={{
                width: "100%", background: "var(--bg3)", border: "1px solid var(--border)",
                borderRadius: "10px", padding: "10px 13px", color: "var(--text)",
                fontSize: "1rem", resize: "none", boxSizing: "border-box",
                outline: "none", fontFamily: "inherit", lineHeight: 1.55,
              }}
            />
            {newQ.trim() && newQScene && (
              <button onClick={postQuestion} disabled={postingQ} style={{
                marginTop: "9px",
                background: accent,
                color: "#fff", border: "none", borderRadius: "9px",
                padding: "8px 20px", fontSize: "0.95rem", fontWeight: 600,
                cursor: "pointer",
              }}>
                {postingQ ? "Posting..." : "Post to group →"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Feed */}
      {loading ? (
        <div style={{ textAlign: "center", color: "var(--text-dim)", fontSize: "0.95rem", padding: "40px 0" }}>
          Loading…
        </div>
      ) : visibleFeed.length === 0 ? (
        <div style={{ textAlign: "center", padding: "52px 24px" }}>
          <div style={{ fontSize: "2.2rem", marginBottom: "14px" }}>💬</div>
          <div style={{ color: "var(--text-dim)", fontSize: "1rem", lineHeight: 1.7 }}>
            {filter === "all"
              ? "No questions yet.\nBe the first to start the conversation."
              : "No questions for this scene yet."}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {visibleFeed.map(q => {
            const isExp  = expanded === q.question_id;
            const qScene = sceneMap[q.scene_id];
            const sceneIdx = (scenes || []).findIndex(s => s.id === q.scene_id);
            return (
              <div key={q.question_id} style={{
                background: "var(--bg2)",
                border: `1px solid ${isExp ? accent + "45" : "var(--border)"}`,
                borderRadius: "18px", overflow: "hidden", transition: "border-color 0.2s",
                boxShadow: "var(--card-shadow)",
              }}>
                <div style={{ padding: "16px 18px 0" }}>
                  <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <Avatar name={q.display_name} size={40} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "1rem", color: "var(--text)", fontWeight: 700 }}>{q.display_name}</span>
                        <span style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>{timeAgo(q.asked_at)}</span>
                        {qScene && (
                          <span style={{
                            fontSize: "0.95rem", color: accent,
                            background: accent + "14", borderRadius: "20px",
                            padding: "1px 9px", border: `1px solid ${accent}30`,
                          }}>
                            Scene {sceneIdx + 1}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "1rem", color: "var(--text)", lineHeight: 1.65 }}>
                        {q.question}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleExpand(q.question_id)}
                    style={{
                      marginTop: "10px", marginLeft: "52px", marginBottom: "12px",
                      background: isExp ? accent + "18" : "transparent",
                      border: `1px solid ${isExp ? accent + "45" : "var(--border)"}`,
                      borderRadius: "20px", padding: "5px 14px",
                      fontSize: "0.95rem", color: isExp ? accent : "var(--text-dim)",
                      cursor: "pointer", display: "inline-flex", gap: "6px", alignItems: "center",
                    }}
                  >
                    <span>💬</span>
                    <span>{q.answer_count} {q.answer_count === 1 ? "reply" : "replies"}</span>
                    <span style={{ fontSize: "0.75rem" }}>{isExp ? "▲" : "▼"}</span>
                  </button>
                </div>

                {isExp && (
                  <div style={{ borderTop: "1px solid var(--border)", padding: "14px 18px", background: "var(--bg3)" }}>
                    {(answers[q.question_id] || []).length === 0 && (
                      <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "14px" }}>No replies yet.</div>
                    )}
                    {(answers[q.question_id] || []).map(a => (
                      <div key={a.answer_id} style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "14px" }}>
                        <Avatar name={a.display_name} size={32} isAi={a.is_ai} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "3px" }}>
                            <span style={{ fontSize: "0.95rem", color: a.is_ai ? "var(--gold)" : "var(--text)", fontWeight: 700 }}>
                              {a.is_ai ? "Gita wisdom" : a.display_name}
                            </span>
                            <span style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>{timeAgo(a.created_at)}</span>
                          </div>
                          <div style={{ fontSize: "1rem", color: "var(--text)", lineHeight: 1.65 }}>{a.text}</div>
                        </div>
                      </div>
                    ))}
                    <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginTop: "6px" }}>
                      <Avatar name={user.name} size={32} />
                      <div style={{ flex: 1 }}>
                        <textarea
                          value={replyText[q.question_id] || ""}
                          onChange={e => setReplyText(prev => ({ ...prev, [q.question_id]: e.target.value }))}
                          placeholder="Write a reply…"
                          rows={2}
                          style={{
                            width: "100%", background: "#fff", border: "1px solid var(--border)",
                            borderRadius: "9px", padding: "8px 11px", color: "var(--text)",
                            fontSize: "1rem", resize: "none", boxSizing: "border-box",
                            outline: "none", fontFamily: "inherit",
                          }}
                        />
                        {(replyText[q.question_id] || "").trim() && (
                          <button onClick={() => postAnswer(q.question_id)} disabled={postingA} style={{
                            marginTop: "7px", background: accent, color: "#fff",
                            border: "none", borderRadius: "8px",
                            padding: "7px 16px", fontSize: "0.95rem", fontWeight: 600, cursor: "pointer",
                          }}>
                            {postingA ? "Replying…" : "Reply →"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCENE PAGE — one scene, full page, beautiful
// ─────────────────────────────────────────────────────────────────────────────
function ScenePage({ scene, sceneIndex, totalScenes, kanda, clubId, user, readSet, onRead, onBack, onNext, onPrev, onOpenCommunity }) {
  const [marking, setMarking] = useState(false);
  const topRef = useRef(null);

  const isRead = readSet.has(scene.id);
  const accent = "#E8832A"; // one accent color (var(--saffron)) across the app

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [scene.id]);

  const markRead = async () => {
    if (isRead || marking) return;
    setMarking(true);
    await fetch(`${API}/ramayana/scene/read`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, scene_id: scene.id }),
    });
    onRead(scene.id);
    setMarking(false);
  };

  const paragraphs = scene.story_text
    ? scene.story_text.split(/\n\n+/).filter(p => p.trim())
    : [];

  return (
    <div ref={topRef} style={{ maxWidth: "680px", margin: "0 auto" }}>
      {/* Top nav */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <button onClick={onBack} style={{
          background: "none", border: "none", color: "var(--text-dim)",
          cursor: "pointer", fontSize: "0.95rem", padding: "0",
          display: "flex", alignItems: "center", gap: "6px",
        }}>
          ← {kanda.name}
        </button>
        <span style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
          {sceneIndex + 1} / {totalScenes}
        </span>
      </div>

      {/* ── Beautiful scene header card ── */}
      <div style={{
        position: "relative", overflow: "hidden",
        borderRadius: "20px", marginBottom: "28px",
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        boxShadow: "var(--card-shadow)",
        padding: "32px 28px 28px",
      }}>
        {/* Big background icon */}
        <div style={{
          position: "absolute", right: "-20px", top: "-20px",
          fontSize: "9rem", opacity: 0.06, userSelect: "none",
          pointerEvents: "none", lineHeight: 1,
        }}>
          {kanda.icon}
        </div>

        {/* Kanda + scene number badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "8px",
          background: accent + "18", border: `1px solid ${accent}40`,
          borderRadius: "20px", padding: "4px 14px",
          fontFamily: "'Inter',sans-serif",
          fontSize: "0.95rem", fontWeight: 600, color: accent,
          marginBottom: "18px",
        }}>
          {kanda.icon} {kanda.name} · Scene {sceneIndex + 1}
        </div>

        {/* Scene title — BIG */}
        <div style={{
          fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "clamp(1.3rem, 4vw, 1.7rem)",
          color: "var(--text)",
          lineHeight: 1.3,
          marginBottom: "14px",
        }}>
          {scene.title}
        </div>

        {/* Emotion */}
        <div style={{
          fontSize: "0.95rem",
          color: accent, fontWeight: 600,
        }}>
          {scene.emotion}
        </div>
      </div>

      {/* ── Story text ── */}
      <div style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: "16px", padding: "24px 22px", marginBottom: "20px",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", lineHeight: 1.9, color: "var(--text)" }}>
          {paragraphs.length > 0 ? (
            paragraphs.map((p, i) => <p key={i} style={{ margin: "0 0 16px" }}>{p}</p>)
          ) : (
            <p style={{ color: "var(--text-dim)", fontSize: "1rem", lineHeight: 1.8 }}>
              {scene.plot_summary}
            </p>
          )}
        </div>
      </div>

      {/* ── Reflection question ── */}
      {scene.emotional_question && (
        <div style={{
          margin: "0 0 20px",
          padding: "20px 22px",
          background: "#fff",
          border: `1px solid ${accent}35`,
          borderRadius: "14px",
          position: "relative",
        }}>
          <div style={{
            position: "absolute", top: "12px", left: "16px",
            fontFamily: "'Inter',sans-serif", fontSize: "2.2rem",
            color: accent + "30", lineHeight: 1, userSelect: "none",
          }}>"</div>
          <p style={{
            margin: 0, paddingLeft: "18px",
            fontFamily: "'Inter',sans-serif",
            fontSize: "1rem", color: accent, fontWeight: 600,
            lineHeight: 1.75,
          }}>
            {scene.emotional_question}
          </p>
        </div>
      )}

      {/* ── Characters ── */}
      {scene.characters?.length > 0 && (
        <div style={{ display: "flex", gap: "7px", flexWrap: "wrap", marginBottom: "28px" }}>
          {scene.characters.map(c => (
            <span key={c} style={{
              fontSize: "0.95rem", color: accent,
              background: accent + "12", borderRadius: "20px",
              padding: "3px 12px", border: `1px solid ${accent}30`,
            }}>
              {c}
            </span>
          ))}
        </div>
      )}

      {/* ── Decorative divider ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
        <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
        <div style={{ width: "7px", height: "7px", background: accent, transform: "rotate(45deg)", opacity: 0.6 }} />
        <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
      </div>

      {/* ── Mark as read ── */}
      {!isRead && (
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <button
            onClick={markRead}
            disabled={marking}
            style={{
              background: accent,
              color: "#fff", border: "none", borderRadius: "12px",
              padding: "13px 36px", fontSize: "1rem", fontWeight: 600,
              cursor: marking ? "wait" : "pointer",
            }}
          >
            {marking ? "Saving..." : "I have read this scene 🙏"}
          </button>
        </div>
      )}

      {isRead && (
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
          <div style={{ fontSize: "0.95rem", color: accent, fontWeight: 600 }}>✓ Read</div>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => onOpenCommunity(scene)}
            style={{
              background: "#fff", border: `1px solid ${accent}`,
              color: accent, borderRadius: "20px",
              padding: "9px 20px", fontSize: "0.95rem", fontWeight: 600,
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "7px",
              transition: "all 0.2s",
            }}
          >
            💬 Group Discussion
          </button>
        </div>
      )}

      {/* ── Prev / Next ── */}
      <div style={{ display: "flex", gap: "12px", marginTop: "32px" }}>
        <button
          onClick={onPrev}
          disabled={sceneIndex === 0}
          style={{
            flex: 1, background: "var(--bg2)", border: "1px solid var(--border)",
            borderRadius: "12px", padding: "13px",
            color: sceneIndex === 0 ? "var(--text-dim)" : "var(--text)",
            fontSize: "0.95rem", cursor: sceneIndex === 0 ? "not-allowed" : "pointer",
            opacity: sceneIndex === 0 ? 0.35 : 1,
          }}
        >
          ← Previous
        </button>
        <button
          onClick={onNext}
          disabled={sceneIndex === totalScenes - 1}
          style={{
            flex: 1,
            background: sceneIndex === totalScenes - 1 ? "var(--bg2)" : accent,
            border: `1px solid ${sceneIndex === totalScenes - 1 ? "var(--border)" : accent}`,
            borderRadius: "12px", padding: "13px",
            color: sceneIndex === totalScenes - 1 ? "var(--text-dim)" : "#fff",
            fontSize: "0.95rem", fontWeight: 600,
            cursor: sceneIndex === totalScenes - 1 ? "not-allowed" : "pointer",
            opacity: sceneIndex === totalScenes - 1 ? 0.35 : 1,
          }}
        >
          Next Scene →
        </button>
      </div>

      {sceneIndex === totalScenes - 1 && (
        <div style={{
          textAlign: "center", fontSize: "0.95rem",
          color: "var(--text-dim)", marginTop: "16px",
        }}>
          You have read the last scene. Return to {kanda.name} for the debate.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DEBATE PAGE
// ─────────────────────────────────────────────────────────────────────────────
function DebatePage({ debate, clubId, user, kanda, onBack }) {
  const [voted,  setVoted]  = useState(debate.user_vote);
  const [votesA, setVotesA] = useState(debate.votes_a);
  const [votesB, setVotesB] = useState(debate.votes_b);
  const [voting, setVoting] = useState(false);
  const accent = "#E8832A"; // one accent color (var(--saffron)) across the app
  const total  = votesA + votesB;
  const pctA   = total ? Math.round((votesA / total) * 100) : 50;

  const castVote = async (side) => {
    if (voted || voting) return;
    setVoting(true);
    const res = await fetch(`${API}/ramayana/debate/vote/${clubId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, side }),
    });
    const d = await res.json();
    setVoted(side); setVotesA(d.votes_a); setVotesB(d.votes_b);
    setVoting(false);
  };

  return (
    <div>
      <button onClick={onBack} style={{
        background: "none", border: "none", color: "var(--text-dim)",
        cursor: "pointer", fontSize: "0.95rem", padding: "0",
        marginBottom: "24px", display: "flex", alignItems: "center", gap: "6px",
      }}>
        ← {kanda.name}
      </button>

      <div style={{
        fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", fontWeight: 700,
        color: "var(--gold)", marginBottom: "8px",
      }}>
        The debate
      </div>

      <div style={{ fontSize: "1.2rem", color: "var(--text)", fontWeight: 700, marginBottom: "6px" }}>
        {debate.character}
      </div>
      <div style={{
        fontSize: "1rem", color: "var(--text-dim)",
        lineHeight: 1.6, marginBottom: "28px",
      }}>
        {debate.question}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginBottom: "28px" }}>
        {[
          { side: "a", data: debate.side_a, color: "#3a7bbf" },
          { side: "b", data: debate.side_b, color: "#b03a2e" },
        ].map(({ side, data, color }) => (
          <div key={side} style={{
            background: voted === side ? color + "14" : "var(--bg2)",
            border: `1.5px solid ${voted === side ? color : "var(--border)"}`,
            borderRadius: "14px", padding: "18px 20px",
            transition: "border-color 0.2s",
            boxShadow: "var(--card-shadow)",
          }}>
            <div style={{
              fontSize: "0.95rem", fontWeight: 700, color: color,
              marginBottom: "10px",
            }}>
              {data.label}
            </div>
            <div style={{
              fontFamily: "'Inter',sans-serif",
              fontSize: "1rem", color: "var(--text-dim)", lineHeight: 1.7,
              marginBottom: voted ? 0 : "14px",
            }}>
              {data.argument}
            </div>
            {!voted && (
              <button
                onClick={() => castVote(side)}
                disabled={voting}
                style={{
                  background: color, color: "#fff", border: "none",
                  borderRadius: "8px", padding: "9px 18px",
                  fontSize: "0.95rem", cursor: "pointer", marginTop: "4px",
                }}
              >
                Vote this side
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Result */}
      {total > 0 && (
        <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px 18px", boxShadow: "var(--card-shadow)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "8px" }}>
            <span style={{ color: "#3a7bbf" }}>{debate.side_a?.label}: {pctA}%</span>
            <span style={{ color: "#b03a2e" }}>{debate.side_b?.label}: {100 - pctA}%</span>
          </div>
          <div style={{ height: "8px", background: "var(--bg3)", borderRadius: "4px", overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${pctA}%`,
              background: "linear-gradient(to right, #3a7bbf, #b03a2e)",
              transition: "width 0.6s",
            }} />
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "8px", textAlign: "center" }}>
            {total} {total === 1 ? "vote" : "votes"} cast in your group
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// JOIN PAGE — for a kanda the user isn't in yet
// ─────────────────────────────────────────────────────────────────────────────
function JoinPage({ user, kanda, onJoined, onBack }) {
  const [joining, setJoining] = useState(false);
  const [error,   setError]   = useState("");
  const accent = "#E8832A"; // one accent color (var(--saffron)) across the app

  const handleJoin = async () => {
    setJoining(true); setError("");
    try {
      const res = await fetch(`${API}/ramayana/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, display_name: user.name, kanda: kanda.id }),
      });
      const d = await res.json();
      if (d.error) setError(d.error);
      else {
        const clubRes = await fetch(`${API}/ramayana/club/${user.id}/${kanda.id}`);
        const clubData = await clubRes.json();
        onJoined(kanda.id, clubData);
      }
    } catch { setError("Could not reach server."); }
    setJoining(false);
  };

  return (
    <div>
      <button onClick={onBack} style={{
        background: "none", border: "none", color: "var(--text-dim)",
        cursor: "pointer", fontSize: "0.95rem", padding: "0", marginBottom: "24px",
      }}>
        ← All Kandas
      </button>

      <div style={{
        position: "relative", overflow: "hidden",
        borderRadius: "20px", padding: "28px 24px",
        marginBottom: "24px",
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        boxShadow: "var(--card-shadow)",
        textAlign: "center",
      }}>
        <div style={{
          position: "absolute", right: "-20px", bottom: "-20px",
          fontSize: "8rem", opacity: 0.06, userSelect: "none", pointerEvents: "none", lineHeight: 1,
        }}>
          {kanda.icon}
        </div>
        <div style={{ fontSize: "3rem", marginBottom: "14px" }}>{kanda.icon}</div>
        <div style={{
          fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "1.3rem", color: "var(--text)",
          marginBottom: "10px",
        }}>
          {kanda.name}
        </div>
        <div style={{
          fontFamily: "'Inter',sans-serif",
          fontSize: "1rem", color: "var(--text-dim)",
          lineHeight: 1.65, marginBottom: "8px",
        }}>
          {kanda.hook}
        </div>
        <div style={{ fontSize: "0.95rem", color: accent, fontWeight: 600 }}>
          {kanda.emotion}
        </div>
      </div>

      <div style={{ fontSize: "1rem", color: "var(--text-dim)", lineHeight: 1.65, marginBottom: "20px" }}>
        You'll be matched with up to 9 other readers for this kanda.
        Read the same scenes. Ask each other questions. Feel the story together.
      </div>

      {error && <div style={{ color: "#dc2626", fontSize: "0.95rem", marginBottom: "10px" }}>{error}</div>}

      <button
        onClick={handleJoin}
        disabled={joining}
        style={{
          width: "100%", background: accent, color: "#fff",
          border: "none", borderRadius: "12px", padding: "14px",
          fontSize: "0.95rem", fontWeight: 700,
          cursor: joining ? "wait" : "pointer",
        }}
      >
        {joining ? "Joining..." : `Join ${kanda.name}`}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT
// ─────────────────────────────────────────────────────────────────────────────
export default function RamayanaClubPage({ user }) {
  const [kandas,   setKandas]   = useState([]);
  const [clubs,    setClubs]    = useState({});       // { kanda_id: clubData }
  const [loading,  setLoading]  = useState(true);

  // Navigation state
  const [page,          setPage]          = useState("home");   // home | kanda | scene | debate | join | community
  const [activeKanda,   setActiveKanda]   = useState(null);
  const [sceneIdx,      setSceneIdx]      = useState(0);
  const [readSets,      setReadSets]      = useState({});       // { kanda_id: Set<scene_id> }
  const [communityScene, setCommunityScene] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    fetch(`${API}/ramayana/kandas`)
      .then(r => r.json())
      .then(kList => {
        setKandas(kList);
        const active = kList.filter(k => !k.coming_soon);
        return Promise.all(
          active.map(k =>
            fetch(`${API}/ramayana/club/${user.id}/${k.id}`)
              .then(r => r.json())
              .then(d => ({ kanda_id: k.id, data: d }))
          )
        );
      })
      .then(results => {
        const clubMap  = {};
        const readMap  = {};
        results.forEach(({ kanda_id, data }) => {
          if (!data.error) {
            clubMap[kanda_id] = data;
            readMap[kanda_id] = new Set(
              (data.scenes || []).filter(s => s.read).map(s => s.id)
            );
          }
        });
        setClubs(clubMap);
        setReadSets(readMap);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user?.id]);

  const myClubKandas = Object.keys(clubs);
  const currentKanda = kandas.find(k => k.id === activeKanda);
  const currentClub  = clubs[activeKanda];
  const currentScenes = currentClub?.scenes || [];
  const currentReadSet = readSets[activeKanda] || new Set();

  const handleSelectKanda = (kandaId) => {
    setActiveKanda(kandaId);
    if (clubs[kandaId]) setPage("kanda");
    else setPage("join");
  };

  const handleOpenScene = (idx) => {
    if (idx === "debate") { setPage("debate"); return; }
    setSceneIdx(idx);
    setPage("scene");
  };

  const handleRead = (sceneId) => {
    setReadSets(prev => {
      const s = new Set(prev[activeKanda] || []);
      s.add(sceneId);
      return { ...prev, [activeKanda]: s };
    });
  };

  const handleJoined = (kandaId, clubData) => {
    setClubs(prev => ({ ...prev, [kandaId]: clubData }));
    setReadSets(prev => ({ ...prev, [kandaId]: new Set() }));
    setPage("kanda");
  };

  if (!user) return null;

  if (loading) return (
    <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-dim)", fontSize: "1rem" }}>
      Loading...
    </div>
  );

  // ── Routing ──
  if (page === "home") {
    return (
      <HomePage
        user={user}
        kandas={kandas}
        myClubKanda={myClubKandas[0]}
        onSelectKanda={handleSelectKanda}
      />
    );
  }

  if (page === "join" && currentKanda) {
    return (
      <JoinPage
        user={user}
        kanda={currentKanda}
        onJoined={handleJoined}
        onBack={() => setPage("home")}
      />
    );
  }

  if (page === "kanda" && currentKanda) {
    return (
      <KandaPage
        user={user}
        kanda={currentKanda}
        clubData={currentClub}
        onJoin={(d) => handleJoined(activeKanda, d)}
        onOpenScene={handleOpenScene}
        onBack={() => setPage("home")}
        onOpenCommunity={() => { setCommunityScene(null); setPage("community"); }}
      />
    );
  }

  if (page === "scene" && currentKanda && currentScenes.length > 0) {
    const scene = currentScenes[sceneIdx];
    return (
      <ScenePage
        scene={scene}
        sceneIndex={sceneIdx}
        totalScenes={currentScenes.length}
        kanda={currentKanda}
        clubId={currentClub?.club_id}
        user={user}
        readSet={currentReadSet}
        onRead={handleRead}
        onBack={() => setPage("kanda")}
        onNext={() => { if (sceneIdx < currentScenes.length - 1) setSceneIdx(sceneIdx + 1); }}
        onPrev={() => { if (sceneIdx > 0) setSceneIdx(sceneIdx - 1); }}
        onOpenCommunity={(s) => { setCommunityScene(s); setPage("community"); }}
      />
    );
  }

  if (page === "community" && currentKanda) {
    return (
      <CommunityPage
        clubId={currentClub?.club_id}
        kanda={currentKanda}
        user={user}
        scenes={currentScenes}
        fromScene={communityScene}
        onBack={() => setPage(communityScene ? "scene" : "kanda")}
      />
    );
  }

  if (page === "debate" && currentKanda && currentClub?.debate) {
    return (
      <DebatePage
        debate={currentClub.debate}
        clubId={currentClub.club_id}
        user={user}
        kanda={currentKanda}
        onBack={() => setPage("kanda")}
      />
    );
  }

  return null;
}
